import React, { useEffect, useState } from "react";
import styles from "./Store.module.css";

import { Link } from "react-router-dom";
import {
  StoreTransactionsData,
  getStoreTransactions,
  getTransferableAmount,
} from "../../services/TransactionServices";
import Dashboard from "../../components/Inputs/Stripe/Dashboard/Dashboard";
import AccountLink from "../../components/Inputs/Stripe/AccountLink/AccountLink";
import { useTranslation } from "react-i18next";
import StatBox from "../../components/StatBox/StatBox";
import {
  createTransfer,
  getStripeAccountStatus,
} from "../../services/StripeServices";
import CreateAccount from "../../components/Inputs/Stripe/CreateAccount/CreateAccount";
import { stripeAccountStatus } from "../../enums/stripeAccountStatusEnum";
import globalStyles from "../../styles/globalStyles.module.css";
import {
  FaArrowRightLong,
  FaMoneyBill,
  FaMoneyBillTransfer,
  FaPiggyBank,
  FaStore,
  FaStripe,
} from "react-icons/fa6";
import { FaRegQuestionCircle, FaShareAltSquare } from "react-icons/fa";
import { Modal } from "react-bootstrap";
import { useTheme } from "../../context/ThemeProvider";
import { CGV } from "../Static/Cgv";

const Store: React.FC = () => {
  const [transactions, setTransactions] = useState<
    (StoreTransactionsData & { type: string })[]
  >([]);
  const [accountStatus, setAccountStatus] = useState<stripeAccountStatus>(
    stripeAccountStatus.not_created
  );
  const [currentMonth, setCurrentMonth] = useState<number>(
    new Date().getMonth() + 1
  );
  const [currentYear, setCurrentYear] = useState<number>(
    new Date().getFullYear()
  );
  const { t } = useTranslation();
  const [transferableAmount, setTransferableAmount] = useState<number>(0);

  // TODO: reduce await time ... store stripeId in local storage
  useEffect(() => {
    const fetch = async () => {
      const status = await getStripeAccountStatus();
      setAccountStatus(status);
      if (status === stripeAccountStatus.completed) {
        const amount = await getTransferableAmount();
        setTransferableAmount(amount);
        const fetchTransactions = await getStoreTransactions(
          currentMonth,
          currentYear
        );
        setTransactions(fetchTransactions);
      }
    };

    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCurrentMonthChange = async (offset: number) => {
    let newCurrentMonth = currentMonth + offset;
    let newCurrentYear = currentYear;
    if (newCurrentMonth < 1) {
      newCurrentMonth = 12;
      newCurrentYear -= 1;
    } else if (newCurrentMonth > 12) {
      newCurrentMonth = 1;
      newCurrentYear += 1;
    }
    setCurrentMonth(newCurrentMonth);
    setCurrentYear(newCurrentYear);

    const alreadyLoaded = transactions.some((t) => {
      const date = new Date(t.updated_at);
      return (
        date.getMonth() + 1 === newCurrentMonth &&
        date.getFullYear() === newCurrentYear
      );
    });
    if (!alreadyLoaded) {
      const fetchTransactions = await getStoreTransactions(
        newCurrentMonth,
        newCurrentYear
      );
      setTransactions((prev) => [
        ...prev,
        ...fetchTransactions.filter(
          (ft) => !prev.some((pt) => pt.id === ft.id)
        ),
      ]);
    }
  };

  const handleTodayClick = async () => {
    const today = new Date();
    setCurrentMonth(today.getMonth() + 1);
    setCurrentYear(today.getFullYear());
  };

  type GroupedTransactions = {
    [date: string]: (StoreTransactionsData & { type: string })[]; // String key (date) and value is an array of transactions
  };

  const groupedTransactions: GroupedTransactions = transactions
    .filter((transaction) => {
      const date = new Date(transaction.updated_at);
      return currentMonth === 0 || date.getMonth() + 1 === currentMonth;
    })
    .reduce((acc, transaction) => {
      const dateStr = new Date(transaction.updated_at).toLocaleDateString();

      if (!acc[dateStr]) {
        acc[dateStr] = [];
      }

      acc[dateStr].push(transaction);

      return acc;
    }, {} as GroupedTransactions);

  const handleTransfer = async () => {
    await createTransfer();
  };

  // const soonAvailableAmount = transactions
  //   .filter((t) => {
  //     const updated = new Date(t.updated_at);
  //     const now = new Date();
  //     const diffDays =
  //       (now.getTime() - updated.getTime()) / (1000 * 60 * 60 * 24);
  //     return diffDays < 14;
  //   })
  //   .reduce((sum, t) => sum + Number(t.net_amount), 0);

  return (
    <>
      {accountStatus === stripeAccountStatus.completed ? (
        <>
          <div className={globalStyles.pageTitle}>Your card shop</div>
          <StatBox
            transactions={transactions}
            onMonthChange={handleCurrentMonthChange}
            onTodayClick={handleTodayClick}
          />

          <div className={styles.displayFlex}>
            <div className={styles.flexColumn}>
              <FaStripe size={64} />
              <div className={styles.displayFlex}>
                <Dashboard />
                <div className={styles.tooltipWrapper}>
                  <span className={styles.tooltipText}>
                    Link to your Stripe account to manage your transactions. And
                    transfer to your bank account.
                  </span>
                  <FaRegQuestionCircle size={24} />
                </div>
              </div>
            </div>
            <div className={styles.flexColumn}>
              <div className={styles.displayFlex}>
                <FaMoneyBillTransfer size={64} /> <h4>Request transfer</h4>
              </div>
              <div className={styles.displayFlex}>
                <button
                  onClick={handleTransfer}
                  className={globalStyles.button}
                >
                  Claim Earnings : {transferableAmount}€
                </button>
                <div className={styles.tooltipWrapper}>
                  <span className={styles.tooltipText}>
                    This is the amount currently available for transfer. You
                    must wait <strong>14 days</strong> after a sale to transfer
                    the earnings.
                  </span>
                  <FaRegQuestionCircle size={24} />
                </div>
              </div>
            </div>
          </div>
          <div className={styles.itemContainer}>
            <h2>{t("transactions")}</h2>
            <div className={styles.item}>
              <p>{t("image")}</p>
              <p>{t("name")}</p>
              <p>{t("qty")}</p>
              <p>{t("amount")}</p>
              <p>{t("net_amount")}</p>
              <p>{t("status")}</p>
            </div>
            {Object.keys(groupedTransactions).map((date) => (
              <div key={date}>
                <h3>{date}</h3>
                {groupedTransactions[date].map((transaction, index) => (
                  <div key={index} className={styles.item}>
                    <img
                      src={transaction.image_id}
                      alt={transaction.name}
                      className={styles.boosterImage}
                    />
                    <Link
                      to={`/boosters/${transaction.booster_id}`}
                      className={styles.link}
                    >
                      <p>{transaction.name}</p>
                    </Link>
                    <p>{transaction.quantity}</p>
                    <p>{transaction.price}€</p>
                    <p>{Number(transaction.net_amount).toFixed(2)}€</p>
                    <p>{transaction.status}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className={globalStyles.pageTitle}>{t("store.guide.title")}</div>
          <div className={`${styles.guideSection}`}>
            <h3>{t("store.guide.step1")}</h3>
            <div className={styles.section}>
              <p className={styles.guideTextSection}>
                <strong>Importez vos images</strong> pour créer des cartes à
                l'aide de l'interface intégrée
              </p>
              <img
                src="/images/guide/card.webp"
                alt="Create a card"
                className={styles.guideImageSection}
              />
            </div>
          </div>
          <div className={`${styles.guideSection}`}>
            <div className={styles.section}>
              <img
                src="/images/guide/booster.webp"
                alt="Create a card"
                className={styles.guideImageSection}
              />
              <p className={styles.guideTextSection}>
                Regrouper vos cartes pour{" "}
                <strong>construire une collection</strong>
              </p>
            </div>
          </div>
          <div className={`${styles.guideSection}`}>
            <h3>{t("store.guide.step2")}</h3>
            <div className={styles.section}>
              <p className={styles.guideTextSection}>
                Indiquez le prix souhaité et{" "}
                <strong>ajoutez votre booster à votre boutique</strong>
              </p>
              <div className={styles.boosterPrice}>
                <div className={styles.storeIcon}>
                  <FaStore size={50} />
                </div>
                <img
                  src="/images/guide/booster.webp"
                  alt="Create a card"
                  className={`${styles.guideImageSection} ${styles.disable}`}
                />
                <strong>10€</strong>
              </div>
            </div>
          </div>
          <div className={`${styles.guideSection}`}>
            <div className={styles.section}>
              <FaShareAltSquare size={80} />
              <div className={styles.guideTextSection}>
                <strong>Partagez</strong> le lien de votre boutique sur les
                réseaux sociaux
              </div>
            </div>
          </div>
          <div className={`${styles.guideSection}`}>
            <h3>{t("store.guide.step3")}</h3>
            <p>
              Les achats complétés peuvent être{" "}
              <strong>transférés sur votre compte bancaire</strong> quand vous
              le souhaitez
            </p>
            <div className={styles.transferSection}>
              <FaStripe size={80} />
              <div className={styles.flex}>
                <FaMoneyBill size={50} />
                <FaArrowRightLong size={80} />
              </div>
              <FaPiggyBank size={80} />
            </div>
          </div>
          <div className={styles.centered}>
            <h2>{t("store.guide.startNow")}</h2>
            <p>{t("stripe.collaborationMsg")}</p>
            {accountStatus === stripeAccountStatus.pending ? (
              <div>
                <AccountLink />
              </div>
            ) : (
              <CreateStore />
            )}
          </div>
        </>
      )}
    </>
  );
};

const CreateStore: React.FC = () => {
  const { currentTheme } = useTheme();
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [acceptCgv, setAcceptCgv] = useState(false);
  const { t } = useTranslation();

  const handleRefuse = () => {
    setModalOpen(false);
    setModalStep(1);
  };

  const handleAccept = () => {
    if (modalStep === 1) {
      // First step accepted, show next step
      setModalStep(2);
      return;
    }
    if (modalStep === 2) {
      // Second step accepted, show next step
      setModalStep(3);
      return;
    }
    // Last step accepted, close modal and reset step
    setModalOpen(false);
  };

  const handleHide = () => {
    setModalOpen(false);
    setModalStep(1);
  };

  return (
    <>
      <button onClick={() => setModalOpen(true)}>Create Store</button>
      <div className={styles.center}>
        <Modal
          show={isModalOpen}
          onHide={handleHide}
          size="sm"
          className={`${currentTheme} d-flex`}
          aria-labelledby="contained-modal-title-vcenter"
          centered
        >
          <div className={styles.modalContainer}>
            {modalStep === 1 && (
              <div>
                <h2>Welcome to Cardonize Store</h2>
                <p>
                  By creating a store, you can sell your card collections and
                  earn money. Please read the following information carefully.
                </p>
                <div className={styles.cgv}>
                  <CGV />
                </div>
                <div className={styles.checkboxContainer}>
                  <input
                    type="checkbox"
                    id="acceptCgv"
                    checked={acceptCgv}
                    onChange={(e) => setAcceptCgv(e.target.checked)}
                  />
                  <label htmlFor="acceptCgv">
                    {" "}
                    I accept the{" "}
                    <Link
                      to="/CGV"
                      className={styles.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      CGV
                    </Link>
                  </label>
                </div>
                <div className={styles.flexLine}>
                  <button
                    onClick={handleRefuse}
                    className={globalStyles.button}
                  >
                    Refuse
                  </button>
                  <button
                    onClick={handleAccept}
                    className={globalStyles.button}
                    disabled={!acceptCgv}
                  >
                    Accept CGV
                  </button>
                </div>
              </div>
            )}
            {modalStep === 2 && (
              <div>
                <div>
                  <h2 className="d-flex align-items-center gap-4">
                    <FaMoneyBill /> Earnings
                  </h2>
                  <p>
                    Cardonize lets you sells your card collections in two
                    formats: <strong>Digital</strong> and <strong>Print</strong>
                    .
                  </p>
                  <table className={styles.tarifTable}>
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Who Sets Price</th>
                        <th>Your Earnings</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <strong>Digital</strong>
                        </td>
                        <td>You</td>
                        <td>95% − €0.50</td>
                        <td>You control pricing of digital boosters</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Print</strong>
                        </td>
                        <td>Cardonize (1€)</td>
                        <td>10% of base price</td>
                        <td>Cardonize handles printing & shipping</td>
                      </tr>
                    </tbody>
                  </table>
                  <p>
                    <strong>Example:</strong> <br></br>If you set a digital
                    booster price of 10€, you earn 9.50€ per sale. For print, if
                    the collection contains 18 cards, you earn 1.8€ per sales.
                  </p>
                  <p>
                    <strong>Prints:</strong> <br></br>
                    By adding an booster to your store, you grant us permission
                    to use it for printing and selling purposes.
                  </p>
                  <p>
                    <strong>Payout Timing:</strong>
                    <br></br>
                    Earnings are held for <strong>14 days</strong> before they
                    become available for transfer to your account.
                  </p>

                  <p>
                    <strong>Disputes & Protection:</strong>
                    <br></br>
                    In case of a dispute, Cardonize will manage the transaction.
                    You will not be charged, and the transfer will be
                    automatically cancelled.
                  </p>
                </div>
                <div className={styles.flexLine}>
                  <button
                    onClick={handleRefuse}
                    className={globalStyles.button}
                  >
                    Refuse
                  </button>
                  <button
                    onClick={handleAccept}
                    className={globalStyles.button}
                  >
                    I understand
                  </button>
                </div>
              </div>
            )}
            {modalStep === 3 && (
              <div>
                <h2 className="d-flex align-items-center gap-4">
                  <FaMoneyBillTransfer /> Setup Stripe
                </h2>
                <p>{t("stripe.collaborationMsg")}</p>
                <p>
                  Sales details information will be available in your Cardonize
                  Store Dashboard
                </p>
                <p>
                  You will need to create a Stripe account to manage your
                  transactions. Make sure to have your personnal informations
                  and bank details ready.
                </p>
                <div className={styles.flexLine}>
                  <button
                    onClick={handleRefuse}
                    className={globalStyles.button}
                  >
                    Refuse
                  </button>
                  <CreateAccount />
                </div>
              </div>
            )}
          </div>
        </Modal>
      </div>
    </>
  );
};

export default Store;
